import React, { useRef, useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    SelectionCard,
    Wrapper,
    CombineInputGroup,
    ActionSection,
    ScrollableContent,
    MobileIcon,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import Config from "../../Config";
import CategoryCard from "../CategoryCard";
import MultiPropSelectModal from "../MultiPropSelectModal";

const AddLead = ({ handleClose, addLeadMutate, key, addLeadLoading, property }) => {
    let formikRef = useRef(null);
    const [propertySelectOpen, setPropertySelectOpen] = useState(false);
    const [height, setHeight] = useState(window.innerHeight);
    const [tempSelectedProps, setTempSelectedProps] = useState([]);
    const [showPropertyModal, setShowPropertyModal] = useState(false);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [key]);

    // Filter out properties with id === 0
    const filteredProperties = property?.filter(prop => prop.id !== 0) || [];

    const initialValues = {
        name: "",
        mobile: "",
        gender: "",
        rentRange: "",
        source: "",
        remarks: "",
        properties: [],
    };

    const rentRange = [
        { label: "0-5K", value: 1 },
        { label: "5-10K", value: 2 },
        { label: "10k-15k", value: 3 },
        { label: "15k-20k", value: 4 },
        { label: "20k-25k", value: 5 },
        { label: "25k-30k", value: 6 },
        { label: "30k-35k", value: 7 },
        { label: "35k-40k", value: 8 },
        { label: "40k-45k", value: 9 },
        { label: "45k-50k", value: 10 },
        { label: "50K+", value: 11 },
    ];

    const source = [
        { value: Config.SOURCE.FACEBOOK, label: 'Facebook' },
        { value: Config.SOURCE.GOOGLE, label: 'Google' },
        { value: Config.SOURCE.INSTAGRAM, label: 'Instagram' },
        { value: Config.SOURCE.FLATMATE, label: 'Flatmate.in' },
        { value: Config.SOURCE.WEBSITE, label: 'Website' },
        { value: Config.SOURCE.MAGICBRICKS, label: 'MagicBricks' },
        { value: Config.SOURCE.ACRES, label: '99acres' },
        { value: Config.SOURCE.HOUSING, label: 'Housing' },
        { value: Config.SOURCE.EQARO, label: 'Eqaro' },
        { value: Config.SOURCE.NOBROKER, label: 'NoBroker' },
        { value: Config.SOURCE.OLX, label: 'Olx' },
        { value: Config.SOURCE.QUIKR, label: 'Quikr' },
        { value: Config.SOURCE.OTHERS, label: 'Others' },
    ];
    const gender = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
    ];

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Name is required')
            .matches(/^[A-Za-z0-9.\s]+$/, 'Only letters, numbers, spaces and dot are allowed.')
            .min(4, 'Name must be at least 4 characters long'),
        mobile: Yup.string()
            .required('Mobile number is required')
            .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits'),
        gender: Yup.number()
            .required('Gender is required')
            .oneOf([1, 2], 'Please select a valid gender'),
        rentRange: Yup.number()
            .required('Rent range is required')
            .oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], 'Please select a valid rent range'),
        source: Yup.number()
            .required('Lead source is required')
            .oneOf(source.map(s => s.value), 'Please select a valid source'),
        properties: Yup.array()
            .required('At least one property is required')
            .min(1, 'At least one property is required'),
        remarks: Yup.string()
            .nullable()
    });

    const handleSubmit = (values, formikHelpers) => {
        const payload = {
            name: values.name,
            mobile: String(values.mobile),
            gender: values.gender,
            rentRange: values.rentRange,
            remarks: values.remarks,
            source: values.source,
            visitType: 0,
            roomType: 0,
            status: 1,
            propId: values.properties,
        };
        addLeadMutate(payload);
    };

    const renderSelectedProperties = (selected = []) => {
        if (!selected.length) return "Select Properties"
        const selectedProperties = filteredProperties?.filter((prop) => selected.includes(prop.id))
        if (!selectedProperties?.length) return "Select Properties"
        const maxToShow = 2
        const namesToShow = selectedProperties.slice(0, maxToShow).map((prop) => prop.name)
        const remainingCount = selectedProperties.length - maxToShow
        return (
            <div className="flex items-center gap-1 truncate">
                {namesToShow.join(", ")}
                {remainingCount > 0 && <span className="ml-1 text-gray-500">+{remainingCount}</span>}
            </div>
        )
    }

    const handleSelectedProperties = (selectedProperties) => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("properties", selectedProperties)
        }
    }
    const handlePropertySave = (properties) => {
        handleSelectedProperties(properties)
        setShowPropertyModal(false)
    }

    return (
        <Container style={{ height: `${height}px`, overflow: "auto" }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Add Lead</h1>
                <button
                    onClick={() => {
                        if (formikRef.current) {
                            formikRef.current.resetForm();
                        }
                        handleClose();
                    }}
                >
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({
                        values,
                        setFieldValue,
                        handleChange,
                        handleBlur,
                        errors,
                        touched,
                        resetForm,
                    }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                <Wrapper>
                                    <InputGroup>
                                        <Label htmlFor="name">Full Name *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="name"
                                                id="name"
                                                className="w-full truncate"
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                        </FieldWrappers>
                                        {errors.name && touched.name && (
                                            <ErrorText>{errors.name}</ErrorText>
                                        )}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="mobile">Mobile No. *</Label>
                                        <FieldWrappers>
                                            <MobileIcon />
                                            <Field
                                                type="text"
                                                id="mobile"
                                                name="mobile"
                                                className="w-full truncate pl-5"
                                                value={values.mobile}
                                                maxLength={10}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,10}$/.test(value)) {
                                                        setFieldValue("mobile", value);
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                            />
                                        </FieldWrappers>
                                        {errors.mobile && touched.mobile && (
                                            <ErrorText>{errors.mobile}</ErrorText>
                                        )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label>Gender *</Label>
                                        <CardWrapper>
                                            {gender.map((mode) => (
                                                <SelectionCard
                                                    key={mode.value}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setFieldValue("gender", mode.value, false);
                                                    }}
                                                    className={
                                                        values.gender === mode.value
                                                            ? "bg-[#26A69A] text-white"
                                                            : "bg-gray-100 text-gray-600"
                                                    }
                                                >
                                                    <span className="text-sm">{mode.label}</span>
                                                </SelectionCard>
                                            ))}
                                        </CardWrapper>
                                        {errors.gender && touched.gender && (
                                            <ErrorText>{errors.gender}</ErrorText>
                                        )}
                                    </InputGroup>

                                    <CombineInputGroup>
                                        <InputGroup>
                                            <Label htmlFor="rentRange">Rent Range *</Label>
                                            <Select
                                                name="rentRange"
                                                id="rentRange"
                                                value={values.rentRange}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className="w-full pl-2"
                                                style={{ height: "46px" }}
                                            >
                                                {rentRange.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.rentRange && touched.rentRange && (
                                                <ErrorText>{errors.rentRange}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="source">Source *</Label>
                                            <Select
                                                name="source"
                                                id="source"
                                                value={values.source}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className="w-full pl-2"
                                                style={{ height: "46px" }}
                                            >
                                                {source.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.source && touched.source && (
                                                <ErrorText>{errors.source}</ErrorText>
                                            )}
                                        </InputGroup>
                                    </CombineInputGroup>

                                    {/* <InputGroup>
                                        <Label>Properties *</Label>
                                        <Select
                                            id="properties"
                                            name="properties"
                                            open={propertySelectOpen}
                                            onOpen={handlePropertySelectOpen}
                                            onClose={() => { }}
                                            value={values.properties || []}
                                            renderValue={renderSelectedProperties}
                                            className="px-2 w-full"
                                            style={{ height: "46px" }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                                disableAutoFocusItem: true,
                                                onClose: (event, reason) => {
                                                    if (reason !== 'backdropClick') {
                                                        setPropertySelectOpen(false);
                                                    }
                                                }
                                            }}
                                        >
                                            <Box sx={{ p: 1 }}>
                                                {filteredProperties.map((prop) => (
                                                    <MenuItem
                                                        key={prop.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            togglePropertySelection(prop.id);
                                                        }}
                                                    >
                                                        <Checkbox checked={tempSelectedProps.includes(prop.id)} />
                                                        <ListItemText primary={prop.name} />
                                                    </MenuItem>
                                                ))}

                                                <Box
                                                    sx={{
                                                        position: 'sticky',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bgcolor: 'background.paper',
                                                        borderTop: '1px solid',
                                                        borderColor: 'divider',
                                                        p: 1,
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        gap: 1,
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handlePropertySelectClose(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handlePropertySelectClose(true)}
                                                        disabled={tempSelectedProps.length === 0}
                                                    >
                                                        OK
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Select>
                                        {errors.properties && touched.properties && (
                                            <ErrorText>{errors.properties}</ErrorText>
                                        )}
                                    </InputGroup> */}

                                    <InputGroup>
                                        <Label>Select property/properties *</Label>
                                        <CategoryCard
                                            type="button"
                                            selectedCategory={values.properties.length > 0 ? {
                                                label: renderSelectedProperties(values.properties),
                                                value: "properties"
                                            } : null}
                                            title="Property/or Properties"
                                            onClick={() => setShowPropertyModal(true)}
                                        />
                                        {errors.properties && touched.properties && (
                                            <ErrorText>{errors.properties}</ErrorText>
                                        )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="remarks">Notes</Label>
                                        <Textarea
                                            id="remarks"
                                            name="remarks"
                                            className="w-full h-20 "
                                            value={values.remarks}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        {errors.remarks && touched.remarks && (
                                            <ErrorText>{errors.remarks}</ErrorText>
                                        )}
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>
                            <ActionSection>
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => {
                                        resetForm();
                                        handleClose();
                                    }}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn type="submit" disabled={addLeadLoading}>
                                    {addLeadLoading ? "Submiting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>
                            {/* Property Selection Modal */}
                            <MultiPropSelectModal
                                isOpen={showPropertyModal}
                                onClose={() => setShowPropertyModal(false)}
                                onSelect={handlePropertySave}
                                options={filteredProperties}
                                selectedProperties={values.properties}
                            />
                        </Form>
                    )}
                </Formik>

            </div>
        </Container>
    );
};

const CardWrapper = tw.div`flex flex-wrap gap-2 my-1.5`;
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const Textarea = tw.textarea`min-h-10 max-h-40 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600 focus:outline-none duration-200 w-full h-20 resize-y break-words overflow-hidden`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default AddLead;